import { DragDropModule } from '@angular/cdk/drag-drop';
import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA, MatLegacyDialogRef } from '@angular/material/legacy-dialog';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import { DataType } from '@mode-switch/express';
import { Encoding } from '@mode-vegas/vegas';
import { ButtonComponent, IconComponent } from '@mode/capra';
import { EncodingHelpers } from '@mode/charts/util-vegas';
import { ChartTypes } from '@mode/shared/contract-common';
import { DialogHeaderComponent, LabeledMenuComponent, MenuItem } from '@mode/shared/ui';
import { getAllowedFormatTypesByCol, getCellRendererFunctionByType, getFormatterByType } from '@mode/shared/util-js';
import { isEqual } from 'lodash';
import { CurrencyFormatPaneComponent } from './currency-format/currency-format-pane.component';
import { DateFormatPaneComponent } from './date-format/date-format-pane.component';
import { NumberFormatPaneComponent } from './number-format/number-format-pane.component';
import { PercentFormatPaneComponent } from './percent-format/percent-format-pane.component';
import * as TextFormattingHelpers from './text-formatting-helpers';
import { UrlFormatPaneComponent } from './url-format/url-format-pane.component';
import { FieldFormatModalOpenAttributes } from '@mode/charts/contract';

export type TextFormattingModalData = FieldFormatModalOpenAttributes;

type FormatTypeMenuItem = MenuItem<ChartTypes.FormatType>;

const ExampleValues = {
  [ChartTypes.FormatType.Default]: ['some string value'],
  [ChartTypes.FormatType.Number]: [1000.123456],
  [ChartTypes.FormatType.Percent]: [3.456789],
  [ChartTypes.FormatType.Financial]: [23456.789, -23456.789],
  [ChartTypes.FormatType.Date]: [Date.now()],
  [ChartTypes.FormatType.Url]: ['https://mode.com'],
  [ChartTypes.FormatType.DatePart]: [1],
};

@Component({
  standalone: true,
  imports: [
    ButtonComponent,
    CommonModule,
    CurrencyFormatPaneComponent,
    DateFormatPaneComponent,
    DialogHeaderComponent,
    DragDropModule,
    IconComponent,
    LabeledMenuComponent,
    NumberFormatPaneComponent,
    PercentFormatPaneComponent,
    UrlFormatPaneComponent,
  ],
  selector: 'mode-text-formatting-modal',
  templateUrl: './text-formatting-modal.component.html',
  styleUrls: ['./text-formatting-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TextFormattingModalComponent {
  readonly Date = ChartTypes.FormatType.Date;
  readonly Currency = ChartTypes.FormatType.Financial;
  readonly Number = ChartTypes.FormatType.Number;
  readonly PlainText = ChartTypes.FormatType.Default;
  readonly Percent = ChartTypes.FormatType.Percent;
  readonly Url = ChartTypes.FormatType.Url;

  originalFormat: ChartTypes.TextFormat;
  format: ChartTypes.TextFormat;
  title = '';
  encoding: Encoding | undefined;
  isDefaultFormatType = false;
  applyFormatFunc: (result: { textFormat: ChartTypes.TextFormat }) => void;

  availableFormatTypes: Array<FormatTypeMenuItem> = [];
  selectedFieldType: FormatTypeMenuItem | undefined;
  exampleFormatting: SafeHtml | undefined;
  errorText = '';

  hasChanges = false;
  isValid = true;

  constructor(
    private sanitizer: DomSanitizer,
    private dialogRef: MatLegacyDialogRef<TextFormattingModalComponent>,
    @Inject(MAT_LEGACY_DIALOG_DATA) data: TextFormattingModalData
  ) {
    this.format = TextFormattingHelpers.resolveInitialFormat(data.colType, data.format);
    this.originalFormat = { ...this.format };

    this.title = data.title;
    this.encoding = data.encoding;
    this.isDefaultFormatType = data.defaultFormatType ?? false;
    this.applyFormatFunc = data.applyCustomFormat;

    this.availableFormatTypes = getAllowedFormatTypesByCol(this.format.colType ?? DataType.Name.Null, true)
      .filter((allowed) => !allowed.disabled)
      .map((allowed) => {
        return { name: allowed.label, value: allowed.value };
      });
    this.updateModalState();
  }

  updateModalState() {
    this.selectedFieldType = this.availableFormatTypes.find((available) => available.value === this.format.type);
    this.hasChanges = !isEqual(this.format, this.originalFormat);
    this.validateFormat();
    this.makeExampleFormatting();
  }

  fieldTypeChange(item: FormatTypeMenuItem) {
    const type = item.value as ChartTypes.FormatType;
    this.update({
      ...TextFormattingHelpers.getBaseFormatByFormatType(type),
      ...this.format,
      type,
    });
  }

  paneFormatChange(updatedFormat: ChartTypes.TextFormat) {
    this.update(updatedFormat);
  }

  update(props: Partial<ChartTypes.TextFormat>) {
    this.format = { ...this.format, ...props };
    this.updateModalState();
  }

  doApply(close: boolean) {
    this.applyFormatFunc({ textFormat: this.format });
    this.originalFormat = { ...this.format };
    this.hasChanges = false;
    if (close) {
      this.close();
    }
  }

  doCancel() {
    this.close();
  }

  doReset() {
    this.format = { ...this.originalFormat };
    this.updateModalState();
  }

  close() {
    this.dialogRef.close();
  }

  makeExampleFormatting() {
    if (this.isValid) {
      const formatter = getFormatterByType(this.format.type);
      const values: (string | number)[] = ExampleValues[this.format.type];

      const renderer = getCellRendererFunctionByType(this.format.type);
      const formattedValues = renderer
        ? values.map((value) => renderer(value, this.format).outerHTML)
        : values.map((value) => formatter(value, this.format));

      this.exampleFormatting = this.sanitizer.bypassSecurityTrustHtml(formattedValues.join(', '));
    }
  }

  validateFormat() {
    this.errorText = '';
    if (this.format.type === ChartTypes.FormatType.Url && this.format.linkURLPattern) {
      // URL is the only type that needs validation currently
      // Validate the linkURLPattern is:
      //   - formed correctly including the proper use of {column_name}
      //   - only references columns in the encoding
      if (this.format.linkURLPattern.match(/[{]{2,}/g) || this.format.linkURLPattern.match(/[}]{2,}/g)) {
        this.errorText = 'The URL provided is invalid.';
      } else {
        // We need to consider removing this method of matching the fields to the referenced columns.
        // Stripping the '[' and ']' from the formula only works for tables. We need to have a more
        // robust method of matching the columns if we are ever going to support this in pivot tables.
        // For now, this is just a direct port of the existing code.
        const availableFields = this.getEncodingFields();
        const referencedColumns = TextFormattingHelpers.extractColumnReferences(this.format.linkURLPattern);
        if (!referencedColumns.every((column: string) => availableFields.includes(column))) {
          this.errorText = 'The column referenced in the URL is not in the table.';
        } else if (this.format.linkURLPattern.split('{').length !== this.format.linkURLPattern.split('}').length) {
          this.errorText = 'The URL provided is invalid.';
        }
      }
    }
    this.isValid = this.errorText.length === 0;
  }

  getEncodingFields() {
    if (this.encoding == null) {
      return [];
    }
    const allFields = EncodingHelpers.collectEncFields(this.encoding);
    return allFields.map((encField) => encField.formula.slice(1, -1));
  }
}
